import { contractsConfig, ContractsNames } from 'services/WalletService/config';
import apiActions from 'store/api/actions';
import store from 'store/configureStore';
import { setActiveModal } from 'store/modals/reducer';
import stakingSelector from 'store/staking/selectors';
import userActionTypes from 'store/user/actionTypes';
import { approveSaga } from 'store/user/sagas/approve';
import { getTokenBalanceSaga } from 'store/user/sagas/getTokenBalance';
import userSelector from 'store/user/selectors';
import { call, put, select, takeLatest } from 'typed-redux-saga';
import { Modals, PoolData, StakingState, UserState } from 'types';
import { StakingAbi } from 'types/contracts/StakingAbi';
import { estimateGasForMethod } from 'utils';
import { AbiItem } from 'web3-utils';

import { depositReward } from '../actions';
import actionTypes from '../actionTypes';
import { updateStakingState } from '../reducer';

export function* depositRewardSaga({
  type,
  payload: { web3Provider, poolId, amount },
}: ReturnType<typeof depositReward>): Generator<unknown, void, any> {
  yield* put(apiActions.request(type));
  const { address: userAddress, network, chainType }: UserState = yield select(userSelector.getUser);
  const { pools }: StakingState = yield select(stakingSelector.getStakingState);

  const { address: stakingContractAddress, abi: stakingAbi } =
    contractsConfig.contracts[ContractsNames.staking][chainType];
  const { address: agtiAddress } = contractsConfig.contracts[ContractsNames.agti][chainType];

  const stakingContract: StakingAbi = yield new web3Provider.eth.Contract(
    stakingAbi as AbiItem[],
    stakingContractAddress[network],
  );

  try {
    yield call(approveSaga, {
      type: userActionTypes.APPROVE,
      payload: {
        web3Provider,
        amount,
        spender: stakingContractAddress[network],
        tokenAddress: agtiAddress[network],
      },
    });

    yield* put(
      setActiveModal({
        activeModal: Modals.SendPendingSimple,
        open: true,
      }),
    );

    const { estimatedGas, gasPrice } = yield* call(estimateGasForMethod, {
      web3Provider,
      contract: stakingContract,
      method: 'depositReward',
      estimateGasArgs: { from: userAddress },
      methodArgs: [poolId, amount],
    });

    const { transactionHash } = yield call(stakingContract.methods.depositReward(poolId, amount).send, {
      from: userAddress,
      gasPrice: Number(gasPrice),
      gas: estimatedGas,
    });

    const newPools = pools.reduce((acc, pool) => {
      if (pool.id === poolId) {
        const changedPool = {
          ...pool,
          totalRewardAmount: amount,
          currentRewardAmount: amount,
        };
        return [...acc, changedPool];
      }
      return [...acc, pool];
    }, [] as PoolData[]);

    yield put(
      updateStakingState({
        pools: newPools,
      }),
    );

    yield call(getTokenBalanceSaga, {
      type: userActionTypes.GET_TOKEN_BALANCE,
      payload: { web3Provider },
    });

    yield* put(
      setActiveModal({
        activeModal: Modals.SendSuccess,
        open: true,
        txHash: transactionHash,
      }),
    );

    yield* put(apiActions.success(type));
  } catch (err) {
    console.error(err);
    yield* put(apiActions.error(type, err));
    yield* put(
      setActiveModal({
        activeModal: Modals.SendRejected,
        open: true,
        repeatHandler: () =>
          store.store.dispatch({
            type,
            payload: {
              web3Provider,
              poolId,
              amount,
            },
          }),
      }),
    );
  }
}

export default function* listener() {
  yield takeLatest(actionTypes.DEPOSIT_REWARD, depositRewardSaga);
}
